/**
 * Auth0 Configuration Validation
 *
 * Domain and clientId are Category A (build-time safe via VITE_ prefix).
 * Audience is Category B (runtime-only, injected by backend via window.__ASH_RUNTIME_CONFIG).
 * See ADR-CEN-20 for configuration management categories.
 * Fast-fail per ADR-CEN-14: missing config crashes at startup with diagnostics.
 */

/** Runtime config injected by the backend into index.html at serve time. */
declare global {
  interface Window {
    __ASH_RUNTIME_CONFIG?: {
      audience?: string
      organization?: string
    }
  }
}

export interface AuthConfig {
  domain: string
  clientId: string
  audience: string
  organization: string
  redirectUri: string
}

export class AuthConfigError extends Error {
  readonly missing: string[]
  readonly hint: string

  constructor(missing: string[], hint: string) {
    super(
      `Auth0 configuration error: missing ${missing.join(', ')}. ${hint}`,
    )
    this.name = 'AuthConfigError'
    this.missing = missing
    this.hint = hint
  }
}

/**
 * Validate and return Auth0 configuration from environment variables.
 *
 * Throws AuthConfigError if required variables are missing (ADR-CEN-14 fast-fail).
 * All consumers MUST call this at startup, not lazily.
 */
export function validateAuthConfig(): AuthConfig {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined
  // Category B (ADR-CEN-20): injected at runtime by the backend,
  // NOT via VITE_ build-time prefix. Read from window.__ASH_RUNTIME_CONFIG.
  const audience = window.__ASH_RUNTIME_CONFIG?.audience
  const organization = window.__ASH_RUNTIME_CONFIG?.organization

  const missing: string[] = []
  if (!domain) missing.push('VITE_AUTH0_DOMAIN')
  if (!clientId) missing.push('VITE_AUTH0_CLIENT_ID')

  if (missing.length > 0) {
    throw new AuthConfigError(
      missing,
      'Set these environment variables in .env.local or your deployment config. ' +
        'See ADR-CEN-20 for configuration management.',
    )
  }

  return {
    domain: domain!,
    clientId: clientId!,
    audience: audience ?? '',
    organization: organization ?? '',
    redirectUri: window.location.origin,
  }
}

/**
 * Public routes that do not require authentication.
 * Per ADR-CEN-12 §Public Routes.
 */
export const PUBLIC_ROUTES = ['/', '/sign-in', '/sign-up', '/api/webhooks'] as const

/**
 * Check if a path is a public route (no auth required).
 */
export function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some(
    (route) => path === route || path.startsWith(route + '/'),
  )
}
